import { queryClient } from '@/tanstack-query.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { OptionsUtil } from '@/modules/common/utils/options.util.ts';
import type { OptionsLoader, StagedEnumOptions } from '@/modules/viewer/types/elements.type.ts';
import { CounterpartyOptionsUtil } from '@/modules/counterparty/utils/counterparty-options.util.ts';
import { CounterpartyQueryOptions } from '@/modules/counterparty/query-options/counterparty.query-options.ts';
import { CounterpartyDistinctField } from '@/modules/counterparty/enums/counterparty-distinct-field.enum.ts';

export class CounterpartyDistinctOptionsUtil {
  private static createDistinctLoader(field: CounterpartyDistinctField): OptionsLoader {
    return async (signal) => {
      const items = await queryClient.ensureQueryData(CounterpartyQueryOptions.getDistinct(signal));
      const options = (items[field] ?? []).map((value): Option => ({ label: value, code: value }));

      return OptionsUtil.sortByLabel(options);
    };
  }

  static segment: StagedEnumOptions = {
    initial: CounterpartyOptionsUtil.segments,
    refined: CounterpartyDistinctOptionsUtil.createDistinctLoader(CounterpartyDistinctField.Segment),
  };

  static region: StagedEnumOptions = {
    initial: CounterpartyOptionsUtil.regions,
    refined: CounterpartyDistinctOptionsUtil.createDistinctLoader(CounterpartyDistinctField.Region),
  };

  static manager: StagedEnumOptions = {
    initial: CounterpartyOptionsUtil.managers,
    refined: CounterpartyDistinctOptionsUtil.createDistinctLoader(CounterpartyDistinctField.Manager),
  };

  static city: OptionsLoader = CounterpartyDistinctOptionsUtil.createDistinctLoader(CounterpartyDistinctField.City);

  static prefetch(signal: AbortSignal): void {
    const staged: StagedEnumOptions[] = [
      CounterpartyDistinctOptionsUtil.segment,
      CounterpartyDistinctOptionsUtil.region,
      CounterpartyDistinctOptionsUtil.manager,
    ];

    for (const options of staged) {
      options.initial(signal).catch(ErrorUtil.reportError);
      options.refined(signal).catch(ErrorUtil.reportError);
    }
  }
}
